import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { PodiumPermissionsDTO, useGetPlatformPermissions } from '@/api/generated';
import { getErrorStatus } from '@/utils/error';

type UsePlatformPermissionsResult = {
  value?: PodiumPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<PodiumPermissionsDTO, unknown>> | undefined;
};

export const usePlatformPermissions = (): UsePlatformPermissionsResult => {
  const {
    data: platformPermissions,
    error,
    isLoading,
    refetch
  } = useGetPlatformPermissions({
    query: { enabled: true }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue = !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? platformPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
